/**
 * The lengths chart. One bar per length; bar height encodes the selected metric (pace / duration /
 * strokes). Active bars are coloured by stroke (not-recorded = dark gray), rests light gray,
 * selection red (fill only — no border). A dashed line marks the average of the active lengths,
 * with a background chip so its label stays readable over the bars. Hover shows a tooltip; clicking
 * routes through store.select (delegated, via data-ordinal). Hand-rolled SVG — no library.
 */
import { store } from '../state/store'
import { formatPace, paceSecondsPer100, type DisplayUnit } from '../analytics/format'
import {
  chartMetric,
  hoveredOrdinal,
  strokeColor,
  strokeLabel,
  STROKES,
  REST_COLOR,
  SELECTED_COLOR,
  type ChartMetric,
} from './uiState'
import type { Length } from '../domain/model'

const HEIGHT = 210
const SLOT = 15
const BAR_W = 11
const PAD_TOP = 16
const AXIS_W = 62

// Pace first and default (see uiState.chartMetric), then duration, then strokes.
const METRICS: { key: ChartMetric; label: string }[] = [
  { key: 'pace', label: 'Pace' },
  { key: 'duration', label: 'Duration' },
  { key: 'strokes', label: 'Strokes' },
]

const AVG_LINE = '#30b0c7'
const AVG_CHIP_INK = '#1a8fa0'

/** The metric value for a length, or null when the metric doesn't apply (pace/strokes on a rest). */
function metricValue(
  l: Length,
  metric: ChartMetric,
  poolM: number,
  unit: DisplayUnit,
): number | null {
  if (metric === 'duration') return l.durationMs
  if (!l.active) return null
  if (metric === 'strokes') return l.strokes
  return paceSecondsPer100(poolM, l.durationMs, unit)
}

function onClick(e: MouseEvent): void {
  const raw = (e.target as SVGElement).getAttribute?.('data-ordinal')
  if (raw === null || raw === undefined) {
    store.clearSelection()
    return
  }
  store.select(Number(raw), { shift: e.shiftKey, toggle: !e.shiftKey })
}

function onMove(e: MouseEvent): void {
  const raw = (e.target as SVGElement).getAttribute?.('data-ordinal')
  const next = raw === null || raw === undefined ? null : Number(raw)
  if (hoveredOrdinal.value !== next) hoveredOrdinal.value = next
}

export function Chart() {
  const activity = store.activity.value
  const stats = store.stats.value
  const poolCm = store.poolLengthCm.value
  if (!activity || !stats || poolCm === null) return null

  const lengths = activity.lengths
  const poolM = poolCm / 100
  const unit = stats.unit
  const metric = chartMetric.value
  const sel = store.selection.value

  const activeValues = lengths
    .filter((l) => l.active)
    .map((l) => metricValue(l, metric, poolM, unit)!)
  const maxActive = Math.max(1, ...activeValues)
  const avgActive = activeValues.reduce((n, v) => n + v, 0) / (activeValues.length || 1)
  const clip = metric === 'duration' ? maxActive * 2 : maxActive * 1.08
  const usable = HEIGHT - PAD_TOP
  const barH = (v: number) => (Math.min(v, clip) / clip) * usable
  const avgY = HEIGHT - barH(avgActive)
  const width = Math.max(lengths.length * SLOT + 8, 120)

  // Active-length numbering, for x-axis ticks and the tooltip.
  const activeNumber = new Map<number, number>()
  let n = 0
  for (const l of lengths) if (l.active) activeNumber.set(l.ordinal, ++n)

  const axisLabel = (v: number, avg: boolean): string => {
    if (metric === 'duration')
      return avg ? `avg ${(v / 1000).toFixed(1)}s` : `${(v / 1000).toFixed(0)}s`
    if (metric === 'strokes') return avg ? `avg ${v.toFixed(1)}` : `${Math.round(v)}`
    return avg ? `avg ${formatPace(v)}` : formatPace(v)
  }

  // Y-axis ticks: minimal, always ending at the slowest/highest. Pace also marks 2:00.
  const ticks: number[] = []
  if (metric === 'pace' && 120 < maxActive) ticks.push(120)
  else if (metric === 'strokes' && 10 < maxActive) ticks.push(10)
  ticks.push(maxActive)

  // Data-driven legend: only strokes that appear among active lengths, plus Rest if any.
  const presentStrokes = new Set<number>()
  let hasRest = false
  for (const l of lengths) {
    if (l.active) presentStrokes.add(l.swimStroke)
    else hasRest = true
  }
  const legend = STROKES.filter((s) => presentStrokes.has(s.value)).map((s) => ({
    label: s.label,
    color: strokeColor(s.value),
  }))
  if (hasRest) legend.push({ label: 'Rest', color: REST_COLOR })

  const avgText = axisLabel(avgActive, true)

  const hov = hoveredOrdinal.value
  const hovLen = hov === null ? null : (lengths.find((l) => l.ordinal === hov) ?? null)
  const hovIndex = hovLen ? lengths.indexOf(hovLen) : 0

  const tipValue = (l: Length): string => {
    if (metric === 'strokes') return l.active ? `${l.strokes} strokes` : '—'
    if (metric === 'pace') {
      const v = metricValue(l, 'pace', poolM, unit)
      return v === null ? '—' : formatPace(v)
    }
    return `${(l.durationMs / 1000).toFixed(1)}s`
  }

  return (
    <div class="card card-pad">
      <div class="chart-head">
        <div class="grow">
          <div class="card-title">Lengths</div>
        </div>
        <div class="seg">
          {METRICS.map((m) => (
            <button
              key={m.key}
              class={`seg-opt${metric === m.key ? ' on' : ''}`}
              onClick={() => (chartMetric.value = m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div class="chart-grid">
        <svg width={AXIS_W} height={HEIGHT} aria-hidden="true">
          <text x={AXIS_W - 4} y={HEIGHT - 2} text-anchor="end" fill="var(--ink-3)" font-size="9">
            {metric === 'duration' ? '0s' : '0'}
          </text>
          {ticks.map((v) => (
            <text
              key={v}
              x={AXIS_W - 4}
              y={HEIGHT - barH(v) + 3}
              text-anchor="end"
              fill="var(--ink-3)"
              font-size="9"
            >
              {axisLabel(v, false)}
            </text>
          ))}
          {/* Average label lives in the gutter (not over the bars), aligned with the dashed line. */}
          <text
            x={AXIS_W - 4}
            y={Math.max(9, avgY + 3)}
            text-anchor="end"
            fill={AVG_CHIP_INK}
            font-size="10"
            font-weight="600"
          >
            {avgText}
          </text>
        </svg>

        <div class="chart-scroll">
          <svg
            width={width}
            height={HEIGHT}
            onClick={onClick}
            onMouseMove={onMove}
            onMouseLeave={() => (hoveredOrdinal.value = null)}
            role="listbox"
            aria-label="Swim lengths"
            style="display:block"
          >
            {ticks.map((v) => (
              <line
                key={v}
                x1="0"
                y1={HEIGHT - barH(v)}
                x2={width}
                y2={HEIGHT - barH(v)}
                stroke="#e5e5ea"
                stroke-width="1"
              />
            ))}
            <line
              x1="0"
              y1={avgY}
              x2={width}
              y2={avgY}
              stroke={AVG_LINE}
              stroke-width="1"
              stroke-dasharray="4 4"
            />
            {lengths.map((l, i) => {
              const v = metricValue(l, metric, poolM, unit)
              const selected = sel.has(l.ordinal)
              const h = v === null ? 3 : Math.max(barH(v), 1)
              const fill = selected
                ? SELECTED_COLOR
                : l.active
                  ? strokeColor(l.swimStroke)
                  : REST_COLOR
              return (
                <rect
                  key={l.ordinal}
                  class={`bar${selected ? ' selected' : ''}`}
                  data-ordinal={l.ordinal}
                  x={i * SLOT + 2}
                  y={HEIGHT - h}
                  width={BAR_W}
                  height={h}
                  rx="1.5"
                  fill={fill}
                />
              )
            })}
          </svg>

          <svg width={width} height="16" style="display:block">
            {lengths.map((l, i) => {
              const num = activeNumber.get(l.ordinal)
              if (num === undefined || (num - 1) % 10 !== 0) return null
              return (
                <text key={l.ordinal} x={i * SLOT + 2} y="12" fill="var(--ink-2)" font-size="10">
                  {num}
                </text>
              )
            })}
          </svg>

          {hovLen && (
            <div class="tooltip" style={`left:${Math.max(0, hovIndex * SLOT - 52)}px`}>
              <div class="t">
                {hovLen.active ? `Length ${activeNumber.get(hovLen.ordinal)}` : 'Rest'}
              </div>
              <div class="v">{tipValue(hovLen)}</div>
              <div class="t">
                {hovLen.active
                  ? `${strokeLabel(hovLen.swimStroke)} · ${hovLen.strokes} strokes`
                  : `${(hovLen.durationMs / 1000).toFixed(1)}s`}
              </div>
            </div>
          )}
        </div>
      </div>

      <div class="legend">
        {legend.map((k) => (
          <span class="k" key={k.label}>
            <span class="dot" style={`background:${k.color}`} />
            {k.label}
          </span>
        ))}
      </div>
    </div>
  )
}
